import S3FileUpload from 'src/helpers/ReactS3Lib/ReactS3';
import { S3BucketConfig } from 'src/common/constants';
import moment from 'moment';
import {
  SeparatedAttachments,
  ServiceRequestAttachment,
  ServiceRequestFormData,
  ServiceRequestPayload,
} from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/ServiceRequestFormDetailsCustomHooks/serviceRequestFormtypes';
import { ServiceRequestTag } from 'src/pages/Reporting/CustomHooks/types';

export type UploadResult =
  | { success: true; url: string; fileName: string }
  | { success: false; error: string; fileName: string };

export type DeleteFileS3Result =
  | { success: true }
  | { success: false; error: string };

const separateAttachments = (
  srAttachments: ServiceRequestAttachment[]
): SeparatedAttachments => {
  const filesToUpload: ServiceRequestAttachment[] = [];
  const existingUrls: string[] = [];

  srAttachments.forEach((attachment: any) => {
    if (attachment.preview) {
      filesToUpload.push(attachment);
    } else {
      existingUrls.push(attachment.url);
    }
  });

  return { filesToUpload, existingUrls };
};

// Prepare service request payload from form data.
export const prepareServiceRequestPayload = (
  serviceRequestFormData: ServiceRequestFormData,
  uploadedAttachments: string[]
): ServiceRequestPayload => {
  let serviceRequestTagging: any[] = [];
  const tags: ServiceRequestTag[] = serviceRequestFormData.tags || [];

  tags.forEach((tag: ServiceRequestTag) => {
    if (tag.__isNew__) {
      serviceRequestTagging.push({ name: tag.value });
    } else {
      serviceRequestTagging.push({ id: tag.value });
    }
  });

  // Serialize attachments.
  const attachmentsString: string =
    uploadedAttachments.length > 0 ? JSON.stringify(uploadedAttachments) : '';

  return {
    ...serviceRequestFormData,
    Attachment: attachmentsString,
    subject: 'Service Request',
    StatusName: 'Submitted',
    tags: serviceRequestTagging,
  };
};

const uploadServiceRequestFileToS3 = async (
  file: any
): Promise<UploadResult> => {
  try {
    if (!file || !file.path) {
      return {
        success: false,
        error: 'Invalid file or file path missing',
        fileName: file?.name || 'unknown',
      };
    }

    // Generate timestamped filename.
    const filenameParts = file.path.split(/\.(?=[^\.]+$)/);
    const newFileName: string =
      filenameParts[0] +
      moment().format('MMDDYYYYhmmss') +
      '.' +
      filenameParts[1];

    const data = await S3FileUpload.uploadFile(
      file,
      S3BucketConfig,
      newFileName
    );

    return {
      success: true,
      url: data.location,
      fileName: newFileName,
    };
  } catch (error: any) {
    console.error('Error uploading file to S3:', error);

    return {
      success: false,
      error: error?.message || 'Upload failed',
      fileName: file?.path || 'unknown',
    };
  }
};

const deleteServiceRequestFileFromS3 = async (
  fileUrl: string
): Promise<DeleteFileS3Result> => {
  try {
    if (!fileUrl) {
      return {
        success: false,
        error: 'File URL is required',
      };
    }

    await S3FileUpload.deleteFile(fileUrl);

    return { success: true };
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || 'Delete failed',
    };
  }
};

export {
  uploadServiceRequestFileToS3,
  deleteServiceRequestFileFromS3,
  separateAttachments,
};
